import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  SquaresFour, Trophy, ChartLineUp, Plus, ListChecks, ChartBar,
  UserCircle, Users, CurrencyDollar, ChatText, Envelope, Gear, SignOut, Stack,
  UsersThree, Headset, CreditCard, MegaphoneSimple,
} from "@phosphor-icons/react";

export default function MobileNav() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Rutas compartidas que mantienen el contexto admin (mismo fix que Sidebar.jsx)
  const ADMIN_CONTEXT_ROUTES = ["/comunidad", "/chat"];
  const isSharedAdminRoute = user?.role === "admin" &&
    ADMIN_CONTEXT_ROUTES.some(r => location.pathname.startsWith(r));
  const isAdminContext = isAdminRoute || isSharedAdminRoute;

  const userLinks = [
    { to: "/dashboard",   labelKey: "nav.home",      icon: SquaresFour },
    { to: "/jugadas",     labelKey: "nav.picks",     icon: Trophy },
    { to: "/parlays",     labelKey: "nav.parlays",   icon: Stack },
    { to: "/comunidad",   labelKey: "nav.community", icon: UsersThree },
    { to: "/profile",     labelKey: "nav.profile",   icon: UserCircle },
  ];

  const adminLinks = [
    { to: "/admin",                labelKey: "nav.panel",           icon: ChartBar },
    { to: "/admin/analisis/nuevo", labelKey: "nav.create",          icon: Plus },
    { to: "/admin/analisis",       labelKey: "nav.management",      icon: ListChecks },
    { to: "/admin/parlays",        labelKey: "nav.parlays",         icon: Stack },
    { to: "/admin/usuarios",       labelKey: "nav.users",           icon: Users },
    { to: "/admin/pagos",          labelKey: "nav.payments",        icon: CurrencyDollar },
    { to: "/admin/metodos-pago",   labelKey: "nav.payment_methods", icon: CreditCard },
    { to: "/admin/soporte",        labelKey: "nav.support",         icon: Headset },
    { to: "/admin/reviews",        labelKey: "nav.reviews",         icon: ChatText },
    { to: "/admin/emails",         labelKey: "nav.emails",          icon: Envelope },
    { to: "/admin/broadcast",      labelKey: "nav.broadcast",       icon: MegaphoneSimple },
    { to: "/admin/config",         labelKey: "nav.config",          icon: Gear },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const links = isAdminContext ? adminLinks : userLinks;

  /* ── Admin mobile nav (scrollable) ── */
  if (isAdminContext) {
    return (
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-obsidian-900/98 backdrop-blur-xl border-t border-white/[0.06]">
        <div
          className="flex overflow-x-auto gap-0 px-0.5 py-1"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {links.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/admin"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 px-3 rounded-lg text-[10px] uppercase tracking-wide font-bold transition-colors flex-shrink-0 min-w-[62px] min-h-[52px] justify-center ${
                  isActive ? "text-yellow-400 bg-yellow-400/10" : "text-zinc-400 hover:text-white"
                }`
              }
              data-testid={`mobilenav-${to.replace(/\//g, "-")}`}
            >
              <Icon className="w-[22px] h-[22px]" weight="regular" />
              {t(labelKey)}
            </NavLink>
          ))}
          {/* Logout — siempre visible al final */}
          <button
            onClick={handleLogout}
            data-testid="mobilenav-logout"
            className="flex flex-col items-center gap-1 py-2 px-3 rounded-lg text-[10px] uppercase tracking-wide font-bold transition-colors flex-shrink-0 min-w-[62px] min-h-[52px] justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <SignOut className="w-[22px] h-[22px]" weight="regular" />
            Salir
          </button>
        </div>
      </nav>
    );
  }

  /* ── User mobile nav (single scrollable row — same as admin) ── */
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-obsidian-900/95 backdrop-blur-xl border-t border-white/[0.06]">
      <div
        className="flex overflow-x-auto gap-0.5 px-1 py-1.5"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {links.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/dashboard"}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-1.5 px-2.5 rounded-md text-[9px] uppercase tracking-wide font-semibold transition-colors flex-shrink-0 min-w-[56px] ${
                isActive ? "text-yellow-400 bg-yellow-400/10" : "text-zinc-500 hover:text-white"
              }`
            }
            data-testid={`mobilenav-${to.replace(/\//g, "-")}`}
          >
            <Icon className="w-[18px] h-[18px]" weight="regular" />
            {t(labelKey)}
          </NavLink>
        ))}
        {/* Salir — al final del scroll */}
        <button
          onClick={handleLogout}
          data-testid="mobilenav-logout"
          className="flex flex-col items-center gap-0.5 py-1.5 px-2.5 rounded-md text-[9px] uppercase tracking-wide font-semibold transition-colors flex-shrink-0 min-w-[56px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <SignOut className="w-[18px] h-[18px]" weight="regular" />
          Salir
        </button>
      </div>
    </nav>
  );
}
